import type { Scene } from "@babylonjs/core/scene";
import type { Mesh } from "@babylonjs/core/Meshes/mesh";
import { CreateIcoSphere } from "@babylonjs/core/Meshes/Builders/icoSphereBuilder";
import { CreateTorus } from "@babylonjs/core/Meshes/Builders/torusBuilder";
import { CreateDisc } from "@babylonjs/core/Meshes/Builders/discBuilder";
import { StandardMaterial } from "@babylonjs/core/Materials/standardMaterial";
import { Color3 } from "@babylonjs/core/Maths/math.color";
import { Vector3 } from "@babylonjs/core/Maths/math.vector";
import { COMBAT_COLORS, hex } from "../palette.ts";

const SPARK_SECONDS = 0.22;
const WAVE_SECONDS = 0.45;
const POPUP_SECONDS = 1.1;

interface Burst {
  mesh: Mesh;
  material: StandardMaterial;
  age: number;
  life: number;
  size: number;
}

interface Popup {
  el: HTMLDivElement;
  anchor: Vector3;
  age: number;
}

export type PopupKind = "score" | "combo" | "info";

/** Projects a world point to CSS pixels; returns false when it is off screen. */
export type Projector = (world: Vector3, out: { x: number; y: number }) => boolean;

/**
 * Short-lived combat effects (GDD §2.3: brief, not too opaque): hit sparks,
 * the Super Slam shockwave and floating "+1" / "x3" texts. Meshes are pooled
 * so a busy fight does not allocate every frame.
 */
export class Effects {
  private readonly scene: Scene;
  private readonly labels: HTMLElement;
  private readonly sparks: Burst[] = [];
  private readonly waves: Burst[] = [];
  /** Cone-shaped Super Slams, one pool per arc width. */
  private readonly cones = new Map<number, Burst[]>();
  private readonly popups: Popup[] = [];
  private readonly point = { x: 0, y: 0 };

  constructor(scene: Scene, labels: HTMLElement) {
    this.scene = scene;
    this.labels = labels;
  }

  spark(at: Vector3, heavy: boolean): void {
    const burst = this.take(this.sparks, () => {
      const mesh = CreateIcoSphere("spark", { radius: 0.28, subdivisions: 1, flat: true }, this.scene);
      return mesh;
    }, heavy ? COMBAT_COLORS.heavy : COMBAT_COLORS.spark);
    burst.mesh.position.copyFrom(at);
    burst.mesh.rotation.set(Math.random() * 3, Math.random() * 3, 0);
    burst.life = SPARK_SECONDS;
    burst.size = heavy ? 1.7 : 1;
  }

  /**
   * The Super Slam going off, with its real shape: a ring for a full circle,
   * a sector along the facing `(fx, fz)` for a cone of `arcDeg` degrees.
   */
  shockwave(at: Vector3, radius: number, arcDeg = 360, fx = 0, fz = 1): void {
    if (arcDeg >= 360) {
      const burst = this.take(this.waves, () => CreateTorus("wave", { diameter: 2, thickness: 0.08, tessellation: 32 }, this.scene), COMBAT_COLORS.slam);
      burst.mesh.position.set(at.x, at.y + 0.08, at.z);
      burst.life = WAVE_SECONDS;
      burst.size = radius;
      return;
    }
    let pool = this.cones.get(arcDeg);
    if (!pool) this.cones.set(arcDeg, (pool = []));
    const fraction = arcDeg / 360;
    const burst = this.take(pool, () => {
      const disc = CreateDisc("cone", { radius: 1, arc: fraction, tessellation: 24 }, this.scene);
      disc.rotation.x = Math.PI / 2;
      disc.bakeCurrentTransformIntoVertices();
      return disc;
    }, COMBAT_COLORS.slam);
    burst.material.backFaceCulling = false;
    burst.mesh.position.set(at.x, at.y + 0.08, at.z);
    // The disc starts at +X and sweeps towards +Z: centre the sweep on the facing.
    burst.mesh.rotation.y = Math.atan2(fx, fz) + fraction * Math.PI - Math.PI / 2;
    burst.life = WAVE_SECONDS;
    burst.size = radius;
  }

  popup(text: string, anchor: Vector3, kind: PopupKind): void {
    const el = document.createElement("div");
    el.className = `popup ${kind}`;
    el.textContent = text;
    this.labels.appendChild(el);
    this.popups.push({ el, anchor: anchor.clone(), age: 0 });
  }

  update(dt: number, project: Projector): void {
    for (const s of this.sparks) {
      if (!this.age(s, dt)) continue;
      const t = s.age / s.life;
      s.mesh.scaling.setAll(s.size * (0.5 + t));
      s.material.alpha = 1 - t;
    }
    for (const w of this.waves) {
      if (!this.age(w, dt)) continue;
      const t = w.age / w.life;
      const r = w.size * (0.25 + 0.75 * Math.sqrt(t));
      w.mesh.scaling.set(r, 1, r);
      w.material.alpha = 0.9 * (1 - t);
    }
    for (const pool of this.cones.values()) {
      for (const c of pool) {
        if (!this.age(c, dt)) continue;
        const t = c.age / c.life;
        const r = c.size * (0.35 + 0.65 * Math.sqrt(t));
        c.mesh.scaling.set(r, 1, r);
        c.material.alpha = 0.55 * (1 - t);
      }
    }
    for (let i = this.popups.length - 1; i >= 0; i--) {
      const p = this.popups[i]!;
      p.age += dt;
      if (p.age >= POPUP_SECONDS) {
        p.el.remove();
        this.popups.splice(i, 1);
        continue;
      }
      p.anchor.y += dt * 0.9;
      const visible = project(p.anchor, this.point);
      p.el.style.visibility = visible ? "visible" : "hidden";
      if (visible) {
        p.el.style.transform = `translate(-50%, -100%) translate(${this.point.x.toFixed(1)}px, ${this.point.y.toFixed(1)}px)`;
        p.el.style.opacity = String(Math.min(1, 3 * (1 - p.age / POPUP_SECONDS)));
      }
    }
  }

  /** Advances a burst; false once it is finished (and hidden). */
  private age(b: Burst, dt: number): boolean {
    if (!b.mesh.isEnabled()) return false;
    b.age += dt;
    if (b.age >= b.life) {
      b.mesh.setEnabled(false);
      return false;
    }
    return true;
  }

  private take(pool: Burst[], create: () => Mesh, color: string): Burst {
    let burst = pool.find((b) => !b.mesh.isEnabled());
    if (!burst) {
      const mesh = create();
      const material = new StandardMaterial("effect", this.scene);
      material.disableLighting = true;
      material.specularColor = Color3.Black();
      mesh.material = material;
      mesh.isPickable = false;
      burst = { mesh, material, age: 0, life: 1, size: 1 };
      pool.push(burst);
    }
    burst.material.emissiveColor = hex(color);
    burst.material.alpha = 1;
    burst.age = 0;
    burst.mesh.setEnabled(true);
    return burst;
  }
}
